import { z } from 'zod'

import type { UserItem } from '../api/models'
import { UserApi } from '../api'
import { getApiConfig } from './api-config'
import {
  nullableDateSchema,
  nullableStringSchema,
  requiredDateSchema,
} from './dal-utils'

const voiceTypeIdToKey = {
  1: 'alto',
  2: 'baritone',
  3: 'bass',
  4: 'mezzoSoprano',
  5: 'soprano',
  6: 'tenor',
  7: 'unknown',
} as const

export type VoiceTypeId = keyof typeof voiceTypeIdToKey

const VoiceType = {
  [voiceTypeIdToKey[1]]: 1,
  [voiceTypeIdToKey[2]]: 2,
  [voiceTypeIdToKey[3]]: 3,
  [voiceTypeIdToKey[4]]: 4,
  [voiceTypeIdToKey[5]]: 5,
  [voiceTypeIdToKey[6]]: 6,
  [voiceTypeIdToKey[7]]: 7,
} as const

const voiceTypeKeys = [
  voiceTypeIdToKey[1],
  voiceTypeIdToKey[2],
  voiceTypeIdToKey[3],
  voiceTypeIdToKey[4],
  voiceTypeIdToKey[5],
  voiceTypeIdToKey[6],
  voiceTypeIdToKey[7],
] as const

export type VoiceTypeKey = (typeof voiceTypeKeys)[number]

const voiceTypeSchema = z
  .preprocess((value) => {
    if (value == null) return null

    if (typeof value === 'number') {
      const key = value as VoiceTypeId
      return voiceTypeIdToKey[key] ?? voiceTypeIdToKey[7]
    }

    return value
  }, z.enum(voiceTypeKeys).nullable())
  .catch(null)

export const userEntitlementSchema = z.object({
  id: nullableStringSchema,
  expireDate: nullableDateSchema,
})

export type UserEntitlement = z.infer<typeof userEntitlementSchema>

export const userSchema = z.preprocess(
  (value) => {
    if (value == null || typeof value !== 'object' || Array.isArray(value)) {
      return value
    }

    const user = value as Record<string, unknown>
    if ('voiceType' in user || !('voiceTypeId' in user)) {
      return value
    }

    return {
      ...user,
      voiceType: user.voiceTypeId,
    }
  },
  z.object({
    id: z.number(),
    email: nullableStringSchema,
    firstName: nullableStringSchema,
    activeWeek: z.number(),
    onboardingCompleted: z.boolean(),
    revenueCatId: nullableStringSchema,
    created: requiredDateSchema,
    entitlement: userEntitlementSchema.nullish().transform((v) => v ?? null),
    mixpanelId: nullableStringSchema,
    voiceType: voiceTypeSchema,
  }),
)

export type User = z.infer<typeof userSchema>

export interface CreateUserParams {
  email: string | null
  firstName: string | null
}

export interface UpdateUserParams {
  email: string | null
  firstName: string | null
  voiceType: VoiceTypeKey | null
}

export async function fetchUser(forceRefresh: boolean) {
  const userApi = new UserApi(getApiConfig())
  const dto = await userApi.apiUserGet({ forceRefresh })
  return userSchema.parse(dto)
}

export async function createUser(params: CreateUserParams) {
  const userApi = new UserApi(getApiConfig())
  const userItem: UserItem = {
    email: params.email,
    firstName: params.firstName,
  }
  const dto = await userApi.apiUserPost({
    userItem,
  })
  return userSchema.parse(dto)
}

export async function updateUser(params: UpdateUserParams) {
  const userApi = new UserApi(getApiConfig())

  const voiceTypeId =
    params.voiceType != null ? VoiceType[params.voiceType] : null

  const userItem: UserItem = {
    email: params.email,
    firstName: params.firstName,
    voiceTypeId,
  }

  await userApi.apiUserPut({
    userItem,
  })
}

export async function deleteAccount() {
  const userApi = new UserApi(getApiConfig())
  await userApi.apiUserRemoveDelete()
}

export async function resendVerificationEmail() {
  const userApi = new UserApi(getApiConfig())
  return await userApi.apiUserEmailverificationPost()
}

export async function completeUserOnboarding() {
  const userApi = new UserApi(getApiConfig())
  await userApi.apiUserCompleteOnboardingPost()
}
